import { hierarchyApi } from '@/modules/hierarchy/api/hierarchy-api'
import {
  partialMatchKey,
  skipToken,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query'

import { apiErrorMessage } from '@/lib/api-error-message'
import { assistantEffects } from '@/lib/assistant-effects.generated'
import {
  type EntitySetting,
  type ResourceSettingDefault,
  type SettingHistoryEntry,
  type SettingHistoryQuery,
} from '@/modules/hierarchy/api/hierarchy-api'
import type { PagedResponse } from '@/lib/api'
import {
  AUDIT_PERMISSIONS,
  HIERARCHY_PERMISSIONS,
  SETTING_PERMISSIONS,
} from '@/lib/permissions'
import { usePermissions, usePermissionsAt } from '@/lib/use-permissions'

import { entityKeys } from './entity-queries'
import {
  resourceSettingDefaultsKey,
  resourceSettingTypeKey,
  settingKeys,
  type SettingTarget,
} from './setting-target'

const assistantEffectMeta = {
  assistantEffects: [
    assistantEffects.hierarchyChanged,
    assistantEffects.settingsChanged,
  ],
} as const

export function useCanReadSettings() {
  const { can } = usePermissions()
  return can(HIERARCHY_PERMISSIONS.read)
}

export function useCanEditSettings(entityId?: string) {
  const { can } = usePermissionsAt(entityId)
  return can(SETTING_PERMISSIONS.edit)
}

export function useCanReadAudit(entityId?: string) {
  const { can } = usePermissionsAt(entityId)
  return can(AUDIT_PERMISSIONS.read)
}

export function useEntitySettingItems(entityId: string | null) {
  const canRead = useCanReadSettings()
  return useQuery({
    queryKey: entityKeys.settings(entityId ?? ''),
    queryFn:
      entityId !== null && canRead
        ? () => hierarchyApi.getEntitySettings(entityId)
        : skipToken,
    meta: assistantEffectMeta,
  })
}

export function useResourceSettingDefaults(entityId: string | null, resourceType: string) {
  const canRead = useCanReadSettings()
  return useQuery({
    queryKey: resourceSettingDefaultsKey(entityId ?? '', resourceType),
    queryFn:
      entityId !== null && canRead
        ? () => hierarchyApi.getResourceSettingDefaults(entityId, resourceType)
        : skipToken,
    meta: assistantEffectMeta,
  })
}

export type SettingProvenance = 'local' | 'policy' | 'inherited' | 'default' | 'unresolved'

export type SettingResolution = {
  provenance: SettingProvenance
  sourceEntityId: string | null
  inheritedDistance: number | null
}

export function deriveSettingResolution(
  entityId: string,
  items: EntitySetting[],
  key: string,
): SettingResolution {
  const item = items.find((i) => i.key === key)
  if (!item) {
    return { provenance: 'unresolved', sourceEntityId: null, inheritedDistance: 0 }
  }
  if (item.isPolicyValue) {
    return { provenance: 'policy', sourceEntityId: null, inheritedDistance: 0 }
  }
  return {
    provenance:
      item.sourceEntityId === null
        ? 'default'
        : item.sourceEntityId === entityId
          ? 'local'
          : 'inherited',
    sourceEntityId: item.sourceEntityId,
    inheritedDistance: item.inheritedDistance,
  }
}

export function deriveResourceSettingResolution(
  {
    source,
    sourceEntityId,
    inheritedDistance,
  }: Pick<ResourceSettingDefault, 'source' | 'sourceEntityId' | 'inheritedDistance'>,
  localEntityId: string | null,
): SettingResolution {
  return {
    provenance: resourceSettingProvenance(source, sourceEntityId, localEntityId),
    sourceEntityId,
    inheritedDistance,
  }
}

function resourceSettingProvenance(
  source: string,
  sourceEntityId: string | null,
  localEntityId: string | null,
): SettingProvenance {
  switch (source) {
    case 'resource':
      return 'local'
    case 'entity':
      return sourceEntityId !== null && sourceEntityId === localEntityId
        ? 'local'
        : 'inherited'
    case 'default':
      return 'default'
    default:
      return 'unresolved'
  }
}

async function saveResourceSetting(target: SettingTarget, value: unknown): Promise<void> {
  switch (target.kind) {
    case 'resource-default':
      return hierarchyApi.putResourceSettingDefault(
        target.entityId,
        target.resourceType,
        target.settingKey,
        value,
      )
    case 'resource':
      return hierarchyApi.putResourceSetting(
        target.entityId,
        target.resourceType,
        target.resourceId,
        target.settingKey,
        value,
      )
    case 'entity':
      throw new Error('Entity settings save through their own batch mutation')
  }
}

function deleteSetting(target: SettingTarget): Promise<void> {
  switch (target.kind) {
    case 'entity':
      return hierarchyApi.deleteEntitySetting(target.entityId, target.settingKey)
    case 'resource-default':
      return hierarchyApi.deleteResourceSettingDefault(
        target.entityId,
        target.resourceType,
        target.settingKey,
      )
    case 'resource':
      return hierarchyApi.deleteResourceSetting(
        target.entityId,
        target.resourceType,
        target.resourceId,
        target.settingKey,
      )
  }
}

type EntityHistoryCriteria = Omit<SettingHistoryQuery, 'after'>

function fetchSettingHistory(
  target: SettingTarget,
  entityCriteria: EntityHistoryCriteria,
  after: string | undefined,
): Promise<PagedResponse<SettingHistoryEntry>> {
  switch (target.kind) {
    case 'entity':
      return hierarchyApi.getSettingHistory(target.entityId, target.settingKey, {
        ...entityCriteria,
        after,
      })
    case 'resource-default':
      return hierarchyApi.getResourceSettingDefaultHistory(
        target.entityId,
        target.resourceType,
        target.settingKey,
        after,
      )
    case 'resource':
      return hierarchyApi.getResourceSettingHistory(
        target.entityId,
        target.resourceType,
        target.resourceId,
        target.settingKey,
        after,
      )
  }
}

// One pass, so a resource whose values and history share a root refetches once.
function invalidateSetting(queryClient: QueryClient, target: SettingTarget) {
  const roots = [settingKeys.values(target), settingKeys.historyRoot(target)]
  return queryClient.invalidateQueries({
    predicate: (query) =>
      roots.some((root) => partialMatchKey(query.queryKey, root)) ||
      resolvesFromDefault(query.queryKey, target),
  })
}

// The server re-resolves the owner's resources of this type in the same write as a
// default change, and a cached resource read does not record its owner.
function resolvesFromDefault(queryKey: QueryKey, target: SettingTarget) {
  return (
    target.kind === 'resource-default' &&
    partialMatchKey(queryKey, resourceSettingTypeKey(target.resourceType))
  )
}

function invalidateEntitySetting(queryClient: QueryClient, entityId: string) {
  void queryClient.invalidateQueries({
    queryKey: entityKeys.settings(entityId),
  })
  void queryClient.invalidateQueries({
    queryKey: entityKeys.settingHistoryByEntity(entityId),
  })
}

export function useSaveResourceSetting() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ target, value }: { target: SettingTarget; value: unknown }) =>
      saveResourceSetting(target, value),
    onSuccess: (_void, { target }) => invalidateSetting(queryClient, target),
  })
}

export function useResetSetting() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ target }: { target: SettingTarget }) => deleteSetting(target),
    onSuccess: (_void, { target }) =>
      target.kind === 'entity'
        ? invalidateEntitySetting(queryClient, target.entityId)
        : invalidateSetting(queryClient, target),
  })
}

export function useSettingHistoryFor(
  target: SettingTarget,
  enabled: boolean,
  entityCriteria: EntityHistoryCriteria = {},
) {
  return useInfiniteQuery({
    queryKey: settingKeys.history(target, entityCriteria),
    queryFn: enabled
      ? ({ pageParam }) => fetchSettingHistory(target, entityCriteria, pageParam || undefined)
      : skipToken,
    initialPageParam: '',
    getNextPageParam: (lastPage) =>
      lastPage.pageInfo.hasNextPage
        ? (lastPage.pageInfo.endCursor ?? undefined)
        : undefined,
    meta: assistantEffectMeta,
  })
}

const SETTING_MUTATION_ERRORS: Readonly<Record<string, string>> = {
  SETTINGS_CHANGE_IN_PROGRESS:
    'Settings are being changed right now. Wait a moment and try again.',
  RESOURCE_NOT_FOUND: 'This item has moved or is no longer active. Reload the page.',
  UNKNOWN_SETTING_KEY: 'This server does not know this setting.',
  UNKNOWN_RESOURCE_TYPE: 'This server does not know this kind of resource.',
  INVALID_SETTING_VALUE: 'The server rejected the value.',
}

export function settingMutationErrorMessage(
  error: unknown,
  fallback: string,
  overrides?: Readonly<Record<string, string>>,
) {
  return apiErrorMessage(error, fallback, {
    codes: { ...SETTING_MUTATION_ERRORS, ...overrides },
  })
}
